import { useState } from "react";
import * as api from "../../api";
import { validateImageFile, readFileAsDataUrl } from "../../lib/imageFiles";

/* P4B-1 (F3) — Admin catálogo: CRUD de PAQUETES + verificationGuide. Movido VERBATIM
   de App.js. `games`/`setGames` permanecen en App.js (compartidos con Home, polling y
   StoreDetailPage) y se inyectan. La lógica de JUEGOS (gameForm, etc.) llega en P4B-2. */
export function useAdminCatalog({ games, setGames, showToast }) {
  const [showAddPkgModal, setShowAddPkgModal] = useState(false);
  const [pkgGameId, setPkgGameId] = useState("");
  const [pkgNameInput, setPkgNameInput] = useState("");
  const [pkgPriceUsdInput, setPkgPriceUsdInput] = useState("");
  const [pkgDescriptionInput, setPkgDescriptionInput] = useState("");
  const [pkgImageInput, setPkgImageInput] = useState(""); // URL or data URL for the package logo
  const [pkgResellerPriceUsdInput, setPkgResellerPriceUsdInput] = useState(""); // wholesale price for role="reseller"
  // Wholesale/provider cost — never shown to client, used to compute
  // profit_amount for the Supabase-backed daily P&L dashboard.
  const [pkgCostPriceUsdInput, setPkgCostPriceUsdInput] = useState("");
  const [pkgRequiresVerify, setPkgRequiresVerify] = useState(false);
  const [pkgVerifyTitle, setPkgVerifyTitle] = useState("");
  const [pkgVerifyDescription, setPkgVerifyDescription] = useState("");
  const [pkgVerifyImageUrl, setPkgVerifyImageUrl] = useState("");
  const [pkgVerifyStepsText, setPkgVerifyStepsText] = useState(""); // one step per line, textarea
  const [pkgIsBestSellerInput, setPkgIsBestSellerInput] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);

  // Accordion state for the admin "Juegos" tab — Set of game.id currently expanded.
  const [expandedGames, setExpandedGames] = useState(() => new Set());
  const toggleExpandedGame = (gameId) => {
    setExpandedGames(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId); else next.add(gameId);
      return next;
    });
  };

  // Game modal (create / edit)
  const [showGameModal, setShowGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null); // game or null
  const [gameForm, setGameForm] = useState({
    name: "", publisher: "", category: "Battle Royale",
    image: "", credentialType: "id", hasAccessMethod: false, accessMethods: [],
    idLabel: "ID del Jugador", idPlaceholder: "Ingresa tu ID",
  });
  const [savingGame, setSavingGame] = useState(false);
  const [deleteGameConfirm, setDeleteGameConfirm] = useState(null);

  const resetPkgForm = () => {
    setPkgNameInput(""); setPkgPriceUsdInput(""); setPkgDescriptionInput("");
    setPkgImageInput("");
    setPkgResellerPriceUsdInput("");
    setPkgCostPriceUsdInput("");
    setPkgRequiresVerify(false);
    setPkgIsBestSellerInput(false);
    setPkgVerifyTitle(""); setPkgVerifyDescription(""); setPkgVerifyImageUrl(""); setPkgVerifyStepsText("");
  };

  const buildVerificationGuidePayload = () => {
    if (!pkgRequiresVerify) return null;
    const steps = pkgVerifyStepsText
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 8);
    return {
      title: (pkgVerifyTitle || "Verifica antes de comprar").trim(),
      description: (pkgVerifyDescription || "").trim(),
      imageUrl: (pkgVerifyImageUrl || "").trim() || null,
      steps,
    };
  };

  const handleSavePackage = async (e) => {
    e.preventDefault();
    if (!pkgNameInput || !pkgPriceUsdInput) {
      showToast("Error", "Completa nombre y precio USD", "error"); return;
    }
    const priceUsd = parseFloat(pkgPriceUsdInput);
    if (isNaN(priceUsd) || priceUsd <= 0) {
      showToast("Error", "Precio USD inválido", "error"); return;
    }
    const description = (pkgDescriptionInput || "").trim();
    const image = (pkgImageInput || "").trim();
    const requiresVerification = !!pkgRequiresVerify;
    const verificationGuide = buildVerificationGuidePayload();
    // Reseller price is optional; blank or 0 → sent as 0 which the backend interprets as "clear it".
    const rp = parseFloat(pkgResellerPriceUsdInput);
    const resellerPriceUsd = !isNaN(rp) && rp > 0 ? rp : 0;
    if (resellerPriceUsd > 0 && resellerPriceUsd >= priceUsd) {
      showToast("Precio revendedor inválido", "El precio revendedor debe ser menor que el precio normal", "error"); return;
    }
    // Wholesale cost (private) — used only to compute profit for Supabase sales.
    const cp = parseFloat(pkgCostPriceUsdInput);
    const costPriceUsd = !isNaN(cp) && cp >= 0 ? cp : 0;
    if (requiresVerification && !verificationGuide.description) {
      showToast("Verificación", "Agrega una descripción para la verificación", "error"); return;
    }
    try {
      if (editingPkg) {
        const updated = await api.editPackage(editingPkg.gameId, editingPkg.pkg.id, {
          name: pkgNameInput, priceUsd, description, image,
          resellerPriceUsd,
          costPriceUsd,
          requiresVerification,
          verificationGuide,
          isBestSeller: !!pkgIsBestSellerInput,
        });
        setGames(games?.map(g => g.id === updated.id ? updated : g));
        showToast("Paquete Actualizado", "Cambios guardados", "success");
      } else {
        const updated = await api.addPackage({
          gameId: pkgGameId, name: pkgNameInput, priceUsd,
          description: description || null,
          image: image || null,
          resellerPriceUsd,
          costPriceUsd,
          requiresVerification,
          verificationGuide,
          isBestSeller: !!pkgIsBestSellerInput,
        });
        setGames(games?.map(g => g.id === updated.id ? updated : g));
        showToast("Paquete Agregado", "Nuevo paquete disponible", "success");
      }
      setShowAddPkgModal(false);
      setEditingPkg(null);
      resetPkgForm();
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleDeletePackage = async (gameId, pkgId) => {
    try {
      const updated = await api.deletePackage(gameId, pkgId);
      setGames(games?.map(g => g.id === updated.id ? updated : g));
      showToast("Eliminado", "Paquete eliminado", "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  // ============ ADMIN: GAMES ============
  const openCreateGameModal = () => {
    setEditingGame(null);
    setGameForm({
      name: "", publisher: "", category: "Battle Royale",
      image: "", credentialType: "id", hasAccessMethod: false, accessMethods: [],
      idLabel: "ID del Jugador", idPlaceholder: "Ingresa tu ID",
    });
    setShowGameModal(true);
  };

  const openEditGameModal = (game) => {
    setEditingGame(game);
    setGameForm({
      name: game.name || "",
      publisher: game.publisher || "",
      category: game.category || "Otros",
      image: game.image || "",
      credentialType: game.credentialType || "id",
      hasAccessMethod: !!game.hasAccessMethod,
      accessMethods: game.accessMethods || [],
      idLabel: game.idLabel || "ID del Jugador",
      idPlaceholder: game.idPlaceholder || "Ingresa tu ID",
    });
    setShowGameModal(true);
  };

  // PASO 3 (F3B): lectores de imagen consolidados en lib/imageFiles.
  const _readImg = async (file, pattern, label, apply) => {
    const v = validateImageFile(file, { pattern, maxMB: 2, label });
    if (!v.ok) { if (!v.silent) showToast(v.title, v.desc, "error"); return; }
    apply(await readFileAsDataUrl(file));
  };
  const handleGameImageFile = (f) => _readImg(f, /^image\/(png|jpe?g|webp|svg\+xml)$/, "Usa PNG, JPG, WEBP o SVG", (d) => setGameForm(prev => ({ ...prev, image: d })));
  const handlePkgImageFile = (f) => _readImg(f, /^image\/(png|jpe?g|webp|svg\+xml|gif)$/, "Usa PNG, JPG, WEBP, GIF o SVG", setPkgImageInput);
  const handlePkgVerifyImageFile = (f) => _readImg(f, /^image\/(png|jpe?g|webp|svg\+xml|gif)$/, "Usa PNG, JPG, WEBP, GIF o SVG", setPkgVerifyImageUrl);

  const handleSaveGame = async (e) => {
    e.preventDefault();
    const name = gameForm.name.trim();
    const image = gameForm.image.trim();
    if (!name) { showToast("Error", "Nombre del juego requerido", "error"); return; }
    if (!image) { showToast("Error", "Debes indicar una imagen/logo (URL o subir archivo)", "error"); return; }
    const needsAccess = gameForm.credentialType === "username_password" && !!gameForm.hasAccessMethod;
    const accessMethods = needsAccess ? (gameForm.accessMethods || []) : [];
    if (needsAccess && accessMethods.length === 0) {
      showToast("Error", "Agrega al menos una forma de acceso (ej. Activision, Google, Facebook)", "error"); return;
    }
    setSavingGame(true);
    try {
      if (editingGame) {
        const updated = await api.editGame(editingGame.id, {
          name, publisher: gameForm.publisher, category: gameForm.category,
          image, credentialType: gameForm.credentialType,
          hasAccessMethod: !!gameForm.hasAccessMethod, accessMethods,
          idLabel: gameForm.idLabel, idPlaceholder: gameForm.idPlaceholder,
        });
        setGames(games?.map(g => g.id === updated.id ? updated : g));
        showToast("Juego Actualizado", updated.name, "success");
      } else {
        const created = await api.createGame({
          name, publisher: gameForm.publisher, category: gameForm.category,
          image, credentialType: gameForm.credentialType,
          hasAccessMethod: !!gameForm.hasAccessMethod, accessMethods,
          idLabel: gameForm.idLabel, idPlaceholder: gameForm.idPlaceholder,
        });
        setGames([...games, created]);
        showToast("Juego Creado", `${created.name} listo para agregar paquetes`, "success");
      }
      setShowGameModal(false);
      setEditingGame(null);
    } catch (err) {
      showToast("Error", err.message, "error");
    } finally {
      setSavingGame(false);
    }
  };

  const handleConfirmDeleteGame = async () => {
    if (!deleteGameConfirm) return;
    try {
      await api.deleteGame(deleteGameConfirm.id);
      setGames(games?.filter(g => g.id !== deleteGameConfirm.id));
      showToast("Juego Eliminado", deleteGameConfirm.name, "success");
      setDeleteGameConfirm(null);
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleUpdateGameCredType = async (gameId, credentialType) => {
    try {
      const updated = await api.editGame(gameId, { credentialType });
      setGames(games?.map(g => g.id === updated.id ? updated : g));
      showToast(
        "Tipo de credencial actualizado",
        `Ahora ${updated.name} usa ${
          credentialType === "username_password" ? "usuario + contraseña"
          : credentialType === "id_server" ? "ID + Server"
          : "solo ID"
        }`,
        "success",
      );
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleToggleGameActive = async (game, nextActive) => {
    try {
      const updated = await api.editGame(game.id, { is_active: !!nextActive });
      setGames(games?.map(g => g.id === updated.id ? updated : g));
      showToast(
        nextActive ? "Juego activado" : "Juego pausado",
        nextActive ? `${updated.name} vuelve a estar disponible en la tienda` : `${updated.name} quedó como "No disponible"`,
        "success",
      );
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  return {
    showAddPkgModal, setShowAddPkgModal,
    pkgGameId, setPkgGameId,
    pkgNameInput, setPkgNameInput,
    pkgPriceUsdInput, setPkgPriceUsdInput,
    pkgDescriptionInput, setPkgDescriptionInput,
    pkgImageInput, setPkgImageInput,
    pkgResellerPriceUsdInput, setPkgResellerPriceUsdInput,
    pkgCostPriceUsdInput, setPkgCostPriceUsdInput,
    pkgRequiresVerify, setPkgRequiresVerify,
    pkgVerifyTitle, setPkgVerifyTitle,
    pkgVerifyDescription, setPkgVerifyDescription,
    pkgVerifyImageUrl, setPkgVerifyImageUrl,
    pkgVerifyStepsText, setPkgVerifyStepsText,
    pkgIsBestSellerInput, setPkgIsBestSellerInput,
    editingPkg, setEditingPkg,
    resetPkgForm, handleSavePackage, handleDeletePackage,
    handlePkgImageFile, handlePkgVerifyImageFile,
    expandedGames, toggleExpandedGame,
    showGameModal, setShowGameModal,
    editingGame, setEditingGame,
    gameForm, setGameForm,
    savingGame, deleteGameConfirm, setDeleteGameConfirm,
    openCreateGameModal, openEditGameModal,
    handleGameImageFile, handleSaveGame, handleConfirmDeleteGame,
    handleUpdateGameCredType, handleToggleGameActive,
  };
}
